<template>
  <div class="date-picker-container">
    <button
      v-if="!isDatePickerOpen"
      class="date-picker-display"
      :class="[
        {
          'date-picker-display-open': isOpen,
          'disabled-picker': isDisableDatepicker,
        },
      ]"
      @click="togglePicker"
      :disabled="isDisableDatepicker"
    >
      <Icon :name="changeBadgeIndicator ? 'badge_expire' : 'badge_indicator'" />
      <span>{{ formattedDate || "Select date" }}</span>
    </button>

    <!-- Calendar as modal or dropdown -->
    <div v-if="isOpen">
      <div
        v-if="props.modal"
        class="date-picker-modal-overlay"
        @click.self="closePicker"
      >
        <div class="date-picker-modal">
          <div class="date-picker-header">
            <div class="date-picker-icon" :class="{ 'disabled-icon': isPrevButtonDisabled }">
              <img
                :src="require('@components/assets/left-icon-dark.svg')"
                alt=""
                class="nav-btn"
                @click="handlePrevMonth"
                :class="{ 'disabled-btn': isPrevButtonDisabled }"
              />
            </div>
            <span class="month-year">{{ monthYear }}</span>
            <div class="date-picker-icon" :class="{ 'disabled-icon': isNextButtonDisabled }">
              <img
                :src="require('@components/assets/right-icon-dark.svg')"
                alt=""
                class="nav-btn"
                @click="handleNextMonth"
                :class="{ 'disabled-btn': isNextButtonDisabled }"
              />
            </div>
          </div>
          <div class="date-picker-search-bar">
            <ExButton
              class="mr-2"
              :outline="'outline-hover'"
              :label="'Today'"
              @onClick="selectToday"
              size="normal"
              :fullwidth="'default'"
              :normal="isTodayDisabled ? 'normal-disable' : 'normal-hover'"
            />
          </div>
          <div class="date-picker-calendar">
            <div class="date-picker-weekdays">
              <span v-for="day in weekdays" :key="day" class="weekday">
                {{ day }}
              </span>
            </div>
            <div class="date-picker-days">
              <span
                v-for="(day, index) in daysInMonth"
                :key="index"
                :class="[
                  'day',
                  {
                    today: isToday(day),
                    selected: isSelected(day),
                    'disabled-picker': isDisabled(day),
                  },
                ]"
                @click="!isDisabled(day) && selectDate(day)"
              >
                <span class="dot" v-if="isToday(day)"></span>
                {{ day }}
              </span>
            </div>
          </div>
          <hr class="hr mt-4" />
          <div class="date-picker-actions">
            <ExButton
              :outline="'outline-hover'"
              :label="'Cancel'"
              @onClick="closePicker"
              size="normal"
              :fullwidth="'default'"
            />
            <ExButton
              :normal="!tempDate || isTempDateDisabled ? 'normal-disable' : 'normal-hover'"
              :disabled="!tempDate || isTempDateDisabled"
              :label="'Apply'"
              @onClick="applyDate"
              size="normal"
              :fullwidth="'default'"
              :loading="$loader.isAppLoading ? 'left' : null"
            />
          </div>
        </div>
      </div>
      <div v-else class="date-picker-dropdown">
        <div class="date-picker-header">
          <div class="date-picker-icon" :class="{ 'disabled-icon': isPrevButtonDisabled }">
            <img
              :src="require('@components/assets/left-icon-dark.svg')"
              alt=""
              class="nav-btn"
              @click="handlePrevMonth"
              :class="{ 'disabled-btn': isPrevButtonDisabled }"
            />
          </div>
          <span class="month-year">{{ monthYear }}</span>
          <div class="date-picker-icon" :class="{ 'disabled-icon': isNextButtonDisabled }">
            <img
              :src="require('@components/assets/right-icon-dark.svg')"
              alt=""
              class="nav-btn"
              @click="handleNextMonth"
              :class="{ 'disabled-btn': isNextButtonDisabled }"
            />
          </div>
        </div>
        <div class="date-picker-search-bar">
          <ExButton
            class="mr-2"
            :outline="'outline-hover'"
            :label="'Today'"
            @onClick="selectToday"
            size="normal"
            :fullwidth="'default'"
          />
        </div>
        <div class="date-picker-calendar">
          <div class="date-picker-weekdays">
            <span v-for="day in weekdays" :key="day" class="weekday">
              {{ day }}
            </span>
          </div>
          <div class="date-picker-days">
            <span
              v-for="(day, index) in daysInMonth"
              :key="index"
              :class="[
                'day',
                { today: isToday(day), selected: isSelected(day) },
              ]"
              @click="selectDate(day)"
            >
              <span class="dot" v-if="isToday(day)"></span>
              {{ day }}
            </span>
          </div>
        </div>
        <hr class="hr mt-4" />
        <div class="date-picker-actions">
          <ExButton
            :outline="'outline-hover'"
            :label="'Cancel'"
            @onClick="closePicker"
            size="normal"
            :fullwidth="'default'"
          />
          <ExButton
            :normal="!tempDate || isTempDateDisabled ? 'normal-disable' : 'normal-hover'"
            :disabled="!tempDate || isTempDateDisabled"
            :label="'Apply'"
            @onClick="applyDate"
            size="normal"
            :fullwidth="'default'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineEmits,
  watch,
  getCurrentInstance,
} from "vue";
import Icon from "@components/icons/Icon.vue";
import ExButton from "@components/buttons/Button.vue";

const props = defineProps({
  value: {
    type: String,
    default: null,
  },
  modal: {
    type: Boolean,
    default: false,
  },
  isDisableDatepicker: {
    type: Boolean,
    default: false,
  },
  isDatePickerOpen: {
    type: Boolean,
    default: false,
  },
  minDate: {
    type: String,
    default: null,
  },
  changeBadgeIndicator: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onApplyDate", "onClose"]);
const app = getCurrentInstance();
const { proxy } = app;
const isOpen = ref(false);
const selectedDate = ref(null);
const tempDate = ref(null);
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());

const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const togglePicker = () => {
  isOpen.value = !isOpen.value;
  if (selectedDate.value) {
    currentMonth.value = selectedDate.value.getMonth();
    currentYear.value = selectedDate.value.getFullYear();
    tempDate.value = selectedDate.value;
  }
};

const closePicker = () => {
  isOpen.value = false;
  tempDate.value = null;
  emit("onClose");
};

const formattedDate = computed(() => {
  const date = props?.value || selectedDate.value;
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;
});

const monthYear = computed(() =>
  new Date(currentYear.value, currentMonth.value).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  })
);

const daysInMonth = computed(() => {
  const days = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay();
  const totalDays = new Date(
    currentYear.value,
    currentMonth.value + 1,
    0
  ).getDate();

  for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
    days.push("");
  }
  for (let i = 1; i <= totalDays; i++) {
    days.push(i);
  }

  return days;
});

const isToday = (day) => {
  const today = new Date();
  return (
    day &&
    today.getDate() === day &&
    today.getMonth() === currentMonth.value &&
    today.getFullYear() === currentYear.value
  );
};
const isTodayDisabled = computed(() => {
  if (!props.minDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const min = new Date(props.minDate);
  min.setHours(0, 0, 0, 0);

  return today < min;
});
const isTempDateDisabled = computed(() => {
  if (!tempDate.value || !props.minDate) return false;

  const selected = new Date(tempDate.value);
  selected.setHours(0, 0, 0, 0);

  const min = new Date(props.minDate);
  min.setHours(0, 0, 0, 0);

  return selected < min;
});

const isPrevButtonDisabled = computed(() => {
  if (!props.minDate) return false;
  
  const min = new Date(props.minDate);
  const current = new Date(currentYear.value, currentMonth.value, 1);
  // Set both dates to first day of month for proper comparison
  min.setDate(1);
  min.setHours(0, 0, 0, 0);
  current.setHours(0, 0, 0, 0);
  // Check if current month is at or before minDate month
  const isDisabled = current <= min;
  return isDisabled;
});

const isNextButtonDisabled = computed(() => {
  // You can add logic here if you want to limit future dates too
  return false;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      const parsed = new Date(newVal);
      if (!isNaN(parsed)) {
        selectedDate.value = parsed;
        tempDate.value = parsed;
        currentMonth.value = parsed.getMonth();
        currentYear.value = parsed.getFullYear();
      }
    } else {
      selectedDate.value = null;
      tempDate.value = null;
    }
  }
);
const isSelected = (day) =>
  day &&
  tempDate.value &&
  tempDate.value.getDate() === day &&
  tempDate.value.getMonth() === currentMonth.value &&
  tempDate.value.getFullYear() === currentYear.value;

const selectDate = (day) => {
  if (day) {
    tempDate.value = new Date(currentYear.value, currentMonth.value, day);
  }
};

const selectToday = () => {
  const today = new Date();
  currentMonth.value = today.getMonth();
  currentYear.value = today.getFullYear();
  tempDate.value = today;
};

const prevMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value -= 1;
  } else {
    currentMonth.value -= 1;
  }
};

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value += 1;
  } else {
    currentMonth.value += 1;
  }
};

const applyDate = () => {
  selectedDate.value = tempDate.value;
  if (props?.isDatePickerOpen) {
    proxy.$loader.setAppLoading(true);
    emit("onApplyDate", tempDate.value);
    setTimeout(() => {
      proxy.$loader.setAppLoading(false);
      closePicker();
    }, 4000);
  } else {
    emit("onApplyDate", tempDate.value);
    closePicker();
  }
};

const handleClickOutside = (event) => {
  const dropdown = document.querySelector(".date-picker-dropdown");
  const displayButton = document.querySelector(".date-picker-display");

  if (
    isOpen.value &&
    !props.modal &&
    dropdown &&
    !dropdown.contains(event.target) &&
    !displayButton.contains(event.target)
  ) {
    closePicker();
  }
};

if (props.value) {
  const parsed = new Date(props.value);
  if (!isNaN(parsed)) {
    selectedDate.value = parsed;
    tempDate.value = parsed;
    currentMonth.value = parsed.getMonth();
    currentYear.value = parsed.getFullYear();
  }
}
const isDisabled = (day) => {
  if (!day || !props.minDate) return false;

  const date = new Date(currentYear.value, currentMonth.value, day);
  const min = new Date(props.minDate);
  min.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date < min;
};

const handlePrevMonth = () => {
  if (!isPrevButtonDisabled.value) {
    prevMonth();
  }
};

const handleNextMonth = () => {
  if (!isNextButtonDisabled.value) {
    nextMonth();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
watch(
  () => props?.isDatePickerOpen,
  (newVal) => {
    isOpen.value = newVal;
  },
  { immediate: true }
);
</script>
<style scoped>
.date-picker-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.date-picker-modal {
  background: white;
  border-radius: 8px;
  padding: 20px;
  min-width: 320px;
  max-width: 95vw;
  font-family: Inter !important;
}
</style>
<style scoped>
.date-picker-container {
  position: relative;
}

.date-picker-display {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  gap: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  font-family: Inter !important;
}
.date-picker-display-open {
  border-radius: var(--radius-md, 8px);
  border: 1px solid #d6bbfb;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #e6ddf9;
}

.date-picker-dropdown {
  position: absolute;
  top: 52px;
  left: 0;
  width: 300px;
  z-index: 1000;
  padding: 16px 0px;
  border-radius: var(--radius-xl, 12px);
  border: 1px solid var(--Colors-Border-border-secondary, #eaecf0);
  background: var(--Colors-Background-bg-primary, #fff);

  /* Shadows/shadow-xl */
  box-shadow: 0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
}

.date-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  margin-bottom: 12px;
  padding: 0px 16px;
}
.date-picker-search-bar {
  margin-bottom: 12px;
  padding: 0px 16px;
}
.date-picker-calendar {
  padding: 0px 16px;
}
.date-picker-icon {
  height: 24px;
  width: 24px;
  border-radius: 100%;
  display: flex;
  justify-items: center;
  align-items: center;
}
.date-picker-icon:hover {
  border-radius: 100%;
  cursor: url("../assets/cursorArrow.svg"), auto;
  background: #f4f4f4;
}
.date-picker-icon.disabled-icon {
  opacity: 0.5;
  cursor: not-allowed;
}
.date-picker-icon.disabled-icon:hover {
  background: none;
  cursor: not-allowed;
}
.date-picker-icon img {
  margin-left: 2px;
}
.date-picker-icon img.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-picker-weekdays,
.date-picker-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}
.month-year {
  color: var(--colors-foreground-fg-secondary-700, #344054);
  text-align: center;

  /* Text md/Semibold */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.weekday {
  margin-bottom: 8px;
  color: #344054;
  text-align: center;

  /* Text sm/Medium */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
}

.day {
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  position: relative;
  color: #344054;
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.day:hover {
  color: #182230;
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  border-radius: 9999px;
  background: #f9fafb;
}

.day.today::after {
  content: "";
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 100%;
  background: #7f56d9;
}

.day.selected {
  border-radius: var(--radius-full, 9999px);
  background: var(--Primary, #7f56d9);
  color: #fff;
  text-align: center;

  /* Text sm/Medium */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  display: flex;
  justify-content: center;
  align-items: center;
}

.date-picker-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding: 0px 16px;
  gap: 12px;
}
.date-picker-dropdown .hr {
  border: 1px solid #f2f4f7;
}
.disabled-picker {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}
.day.disabled-picker {
  color: #d0d5dd;
  pointer-events: none;
  background: none !important;
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
